import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * CreateLeaseModal
 *
 * Creates a financial lease agreement for an asset.
 * Opened from AssetDetail.jsx → lease panel → "Create lease" button.
 *
 * Props:
 *   open       {boolean}
 *   onClose    {() => void}
 *   onSuccess  {(lease) => void}
 *   asset      {object}   — asset being leased (id, serial_number, asset_type, owner_org_id)
 *   token      {string}
 *   activeOrg  {object|null}
 *
 * API:
 *   POST /api/v1/inventory/leases
 *
 * Flow: 2-step — Configure → Confirm → Done
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HandshakeIcon from "@mui/icons-material/Handshake";
import axios from "axios";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

const BILLING_CYCLES = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "ANNUALLY", label: "Annually" },
];

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];

const EMPTY_FORM = {
  lessor_org_id: "",
  lessee_org_id: "",
  start_date: "",
  end_date: "",
  billing_cycle: "MONTHLY",
  rate: "",
  currency: "INR",
  contract_reference: "",
  remarks: "",
};

export default function CreateLeaseModal({
  open,
  onClose,
  onSuccess,
  asset,
  token,
  activeOrg,
}) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  // ── Reset on open ──
  useEffect(() => {
    if (open && asset) {
      setStep(0);
      setForm({
        ...EMPTY_FORM,
        // Pre-fill lessor as the asset's current owner org
        lessor_org_id: asset.owner_org_id || "",
      });
      setErrors({});
      setSubmitting(false);
      setResult(null);
    }
  }, [open, asset]);

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: "" }));
  };

  // ── Validate step 0 ──
  const validate = () => {
    const e = {};
    const uuidRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (!form.lessor_org_id.trim())
      e.lessor_org_id = "Lessor org ID is required";
    else if (!uuidRe.test(form.lessor_org_id.trim()))
      e.lessor_org_id = "Must be a valid UUID";

    if (!form.lessee_org_id.trim())
      e.lessee_org_id = "Lessee org ID is required";
    else if (!uuidRe.test(form.lessee_org_id.trim()))
      e.lessee_org_id = "Must be a valid UUID";

    if (form.lessor_org_id.trim() === form.lessee_org_id.trim())
      e.lessee_org_id = "Lessor and lessee cannot be the same org";

    if (!form.start_date) e.start_date = "Start date is required";
    if (!form.end_date) e.end_date = "End date is required";
    if (form.start_date && form.end_date && form.start_date >= form.end_date)
      e.end_date = "End date must be after start date";

    if (!form.rate || isNaN(form.rate) || parseFloat(form.rate) <= 0)
      e.rate = "Rate must be a positive number";

    return e;
  };

  const handleContinue = () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setStep(1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/leases`,
        {
          inventory_asset_id: asset.id,
          lessor_org_id: form.lessor_org_id.trim(),
          lessee_org_id: form.lessee_org_id.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          billing_cycle: form.billing_cycle,
          rate: parseFloat(form.rate),
          currency: form.currency,
          ...(form.contract_reference && {
            contract_reference: form.contract_reference.trim(),
          }),
          ...(form.remarks && { remarks: form.remarks.trim() }),
        },
        { headers: authHeaders },
      );

      setResult(res.data.lease);
      setStep(2);
      onSuccess(res.data.lease);
    } catch (err) {
      setErrors({
        _submit: err.response?.data?.error || "Failed to create lease",
      });
      setSubmitting(false);
    }
  };

  if (!asset) return null;

  // ── Summary rows for confirm step ──
  const summaryRows = [
    { label: "Asset", value: `${asset.serial_number} · ${asset.asset_type}` },
    { label: "Lessor org", value: form.lessor_org_id, mono: true },
    { label: "Lessee org", value: form.lessee_org_id, mono: true },
    { label: "Start date", value: form.start_date },
    { label: "End date", value: form.end_date },
    {
      label: "Rate",
      value: `${form.currency} ${parseFloat(form.rate || 0).toLocaleString("en-IN")} / ${form.billing_cycle?.toLowerCase()}`,
    },
    ...(form.contract_reference
      ? [{ label: "Contract ref", value: form.contract_reference }]
      : []),
    ...(form.remarks ? [{ label: "Remarks", value: form.remarks }] : []),
  ];

  return (
    <Dialog
      open={open}
      onClose={step === 2 ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 4,
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
          // borderRadius: 5,

          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",

          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="#fff">
              Create lease
            </Typography>
            <Typography
              variant="body2"
              
              mt={0.3}
              color="#b4b1b1ff"
            >
              {asset.serial_number} · {asset.asset_type}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon sx={{ color: "#fff" }} fontSize="small" />
          </IconButton>
        </Box>
        {step < 2 && (
          <Stepper activeStep={step} sx={{ mt: 2, mb: 0.5 }} alternativeLabel>
            {["Configure", "Confirm"].map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      color: "#fff",
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </DialogTitle>

      {/* ── Step 0: Configure ── */}
      {step === 0 && (
        <>
          <DialogContent dividers sx={{ pt: 2.5 }}>
            <Stack spacing={2.5}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
              >
                Parties
              </Typography>

              <TextField
                label="Lessor org ID"
                size="small"
                fullWidth
                required
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={form.lessor_org_id}
                onChange={(e) => handleChange("lessor_org_id", e.target.value)}
                error={!!errors.lessor_org_id}
                helperText={
                  errors.lessor_org_id ||
                  "Org leasing the asset out (usually asset owner)"
                }
                inputProps={{
                  style: { fontFamily: "monospace", fontSize: 13 },
                }}
              />

              <TextField
                label="Lessee org ID"
                size="small"
                fullWidth
                required
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={form.lessee_org_id}
                onChange={(e) => handleChange("lessee_org_id", e.target.value)}
                error={!!errors.lessee_org_id}
                helperText={
                  errors.lessee_org_id || "Org paying to use the asset"
                }
                inputProps={{
                  style: { fontFamily: "monospace", fontSize: 13 },
                }}
              />

              <Divider />
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
              >
                Lease period
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <TextField
                  label="Start date"
                  size="small"
                  type="date"
                  required
                  value={form.start_date}
                  onChange={(e) => handleChange("start_date", e.target.value)}
                  error={!!errors.start_date}
                  helperText={errors.start_date}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End date"
                  size="small"
                  type="date"
                  required
                  value={form.end_date}
                  onChange={(e) => handleChange("end_date", e.target.value)}
                  error={!!errors.end_date}
                  helperText={errors.end_date}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Divider />
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
              >
                Financial terms
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                <TextField
                  label="Rate"
                  size="small"
                  required
                  type="number"
                  value={form.rate}
                  onChange={(e) => handleChange("rate", e.target.value)}
                  error={!!errors.rate}
                  helperText={errors.rate}
                  inputProps={{ min: 0, step: "0.01" }}
                />
                <TextField
                  select
                  label="Currency"
                  size="small"
                  value={form.currency}
                  onChange={(e) => handleChange("currency", e.target.value)}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Billing cycle"
                  size="small"
                  value={form.billing_cycle}
                  onChange={(e) =>
                    handleChange("billing_cycle", e.target.value)
                  }
                >
                  {BILLING_CYCLES.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Divider />
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
              >
                Reference (optional)
              </Typography>

              <TextField
                label="Contract reference"
                size="small"
                fullWidth
                placeholder="CONTRACT-2024-001"
                value={form.contract_reference}
                onChange={(e) =>
                  handleChange("contract_reference", e.target.value)
                }
              />

              <TextField
                label="Remarks"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={form.remarks}
                onChange={(e) => handleChange("remarks", e.target.value)}
                placeholder="Optional notes about this lease…"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={onClose}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
           <Button
  variant="contained"
  disableElevation
  onClick={handleContinue}
  sx={{
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 2,

    background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
    boxShadow: "0 8px 20px rgba(245,158,11,0.25)",

    "&:hover": {
      background: "linear-gradient(135deg, #FCD34D, #D97706)",
    },
  }}
>
  Continue
</Button>
          </DialogActions>
        </>
      )}

      {/* ── Step 1: Confirm ── */}
      {step === 1 && (
        <>
          <DialogContent dividers sx={{ pt: 2.5 }}>
            {errors._submit && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                {errors._submit}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Review the lease terms before confirming.
            </Typography>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {summaryRows.map((row, i) => (
                <Box
                  key={i}
                  display="flex"
                  px={2}
                  py={1.2}
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                    bgcolor:
                      i % 2 === 0 ? "background.default" : "background.paper",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 150 }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontFamily: row.mono ? "monospace" : "inherit",
                      fontSize: row.mono ? 12 : "inherit",
                    }}
                  >
                    {row.value || "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setStep(0);
                setErrors({});
                setSubmitting(false);
              }}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              disableElevation
              color="primary"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <HandshakeIcon fontSize="small" />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 160,
              }}
            >
              {submitting ? "Creating…" : "Confirm lease"}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ── Step 2: Done ── */}
      {step === 2 && result && (
        <>
          <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
            <Box textAlign="center" py={2}>
              <CheckCircleIcon
                sx={{ fontSize: 52, color: "success.main", mb: 2 }}
              />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Lease created
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Lease agreement for <strong>{asset.serial_number}</strong> is
                now active.
              </Typography>
              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "left",
                }}
              >
                {[
                  { label: "Lease ID", value: result.id, mono: true },
                  { label: "Status", value: result.lease_status },
                  {
                    label: "Period",
                    value: `${result.start_date} → ${result.end_date}`,
                  },
                  {
                    label: "Rate",
                    value: `${result.currency} ${parseFloat(result.rate).toLocaleString("en-IN")} / ${result.billing_cycle?.toLowerCase()}`,
                  },
                ].map((row, i) => (
                  <Box
                    key={i}
                    display="flex"
                    justifyContent="space-between"
                    py={0.8}
                    sx={{
                      borderBottom: i < 3 ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {row.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        fontFamily: row.mono ? "monospace" : "inherit",
                        fontSize: row.mono ? 11 : 12,
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onClose}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
